import avatarAssetsData from '../assets/avatarAssets.json';

/**
 * Avatar assets manager for loading avatar icons and VRM model paths
 * Now supports loading from server with fallback to local JSON
 */
class AvatarAssetsManager {
  constructor() {
    this.data = avatarAssetsData; // Local fallback data
    this.serverData = null; // Server data when available
    this.cache = new Map();
    this.serverUrl = 'https://vp.thefanime.com/dont-delete/avatarAssets.json';
    this.isLoading = false;
    this.hasLoadedFromServer = false;
    this.loadPromise = null;
    
    // Automatically try to load from server on initialization
    this.loadFromServer();
  }

  /**
   * Load avatar data from server
   * @returns {Promise} Promise that resolves when data is loaded
   */
  async loadFromServer() {
    if (this.isLoading) {
      return this.loadPromise;
    }
    
    if (this.hasLoadedFromServer) {
      return Promise.resolve(this.serverData || this.data);
    }
    
    this.isLoading = true;
    this.loadPromise = this._fetchServerData();
    
    try {
      await this.loadPromise;
    } finally {
      this.isLoading = false;
    }
    
    return this.getCurrentData();
  }

  /**
   * Internal method to fetch data from server
   * @private
   */
  async _fetchServerData() {
    try {
      console.log('🌐 Loading avatar assets from server:', this.serverUrl);
      
      // Add cache-busting parameter to URL instead of using headers
      const cacheBuster = Date.now();
      const urlWithCacheBuster = `${this.serverUrl}?_cb=${cacheBuster}`;
      
      const response = await fetch(urlWithCacheBuster, {
        method: 'GET',
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const serverData = await response.json();
      
      // Validate server data structure
      if (!this._validateData(serverData)) {
        throw new Error('Invalid server data structure');
      }
      
      this.serverData = serverData;
      this.hasLoadedFromServer = true;
      this.cache.clear(); // Clear cache when new data is loaded
      
      console.log('✅ Avatar assets loaded successfully from server');
      return serverData;
      
    } catch (error) {
      console.warn('⚠️ Failed to load avatar assets from server:', error.message);
      console.log('📁 Using local fallback data');
      
      // Mark as attempted to avoid repeated failed requests
      this.hasLoadedFromServer = true;
      return this.data;
    }
  }

  /**
   * Validate avatar data structure
   * @private
   * @param {Object} data - Data to validate
   * @returns {boolean} True if valid
   */
  _validateData(data) {
    return (
      data &&
      Array.isArray(data.avatars) &&
      data.avatars.length > 0 &&
      data.avatars[0].id &&
      data.avatars[0].src &&
      data.avatars[0].vrmPath &&
      data.defaults &&
      data.defaults.fallbackAvatar
    );
  }

  /**
   * Get current data (server data if available, otherwise local)
   * @returns {Object} Current avatar data
   */
  getCurrentData() {
    return this.serverData || this.data;
  }

  /**
   * Force reload from server
   * @returns {Promise} Promise that resolves when data is reloaded
   */
  async reload() {
    this.hasLoadedFromServer = false;
    this.serverData = null;
    this.cache.clear();
    return this.loadFromServer();
  }

  /**
   * Get all primary avatars
   * @returns {Array} Array of avatar objects with id, name, src, vrmPath
   */
  getAllAvatars() {
    return this.getCurrentData().avatars;
  }

  /**
   * Get all avatars including alternatives for display grids
   * @returns {Array} Combined array of primary and alternative avatars
   */
  getAllAvatarsWithAlternatives() {
    const currentData = this.getCurrentData();
    return [...currentData.avatars, ...currentData.alternativeAvatars];
  }

  /**
   * Get all Live2D avatars
   * @returns {Array} Array of Live2D avatar objects
   */
  getLive2dAvatars() {
    const currentData = this.getCurrentData();
    return currentData.live2dAvatars || [];
  }

  /**
   * Get avatar by ID (supports legacy IDs)
   * @param {string} id - Avatar ID
   * @returns {Object|null} Avatar object or null if not found
   */
  getAvatarById(id) {
    if (this.cache.has(id)) {
      return this.cache.get(id);
    }

    const currentData = this.getCurrentData();

    // Mapping from regular avatar IDs to Live2D IDs
    const regularToLive2dMapping = {
      'm1': 'm1_live2d',
      'm2': 'm2_live2d', 
      'm3': 'm3_live2d',
      'm4': 'm4_live2d'
    };

    // Check if we need to map the ID
    const live2dId = regularToLive2dMapping[id] || id;

    // Check Live2D avatars first
    let avatar = currentData.live2dAvatars.find(a => a.id === live2dId);
    
    // Check primary avatars if not found in Live2D
    if (!avatar) {
      avatar = currentData.avatars.find(a => a.id === id);
    }
    
    // Check alternative avatars
    if (!avatar) {
      avatar = currentData.alternativeAvatars.find(a => a.id === id);
    }
    
    // Check legacy mapping
    if (!avatar && currentData.legacyMapping[id]) {
      const legacyData = currentData.legacyMapping[id];
      avatar = {
        id: id,
        name: `Legacy ${id}`,
        src: legacyData.src,
        vrmPath: legacyData.vrmPath
      };
    }
    
    // Cache the result
    if (avatar) {
      this.cache.set(id, avatar);
    }
    
    return avatar;
  }

  /**
   * Get VRM path for an avatar with validation
   * @param {string|Object} avatar - Avatar ID or avatar object
   * @returns {string} VRM file path
   */
  getVrmPath(avatar) {
    console.log('[AvatarAssets] getVrmPath called with:', avatar);
    console.log('[AvatarAssets] Current data available:', this.getCurrentData());
    
    if (typeof avatar === 'string') {
      // Avatar is an ID string
      console.log('[AvatarAssets] Avatar is string, looking up by ID:', avatar);
      const avatarObj = this.getAvatarById(avatar);
      console.log('[AvatarAssets] Found avatar object:', avatarObj);
      const vrmPath = avatarObj ? avatarObj.vrmPath : this.getCurrentData().defaults.fallbackAvatar.vrmPath;
      console.log('[AvatarAssets] Raw VRM path:', vrmPath);
      const fullUrl = this.getFullAssetUrl(vrmPath);
      console.log('[AvatarAssets] Full VRM URL:', fullUrl);
      return fullUrl;
    }
    
    if (avatar && typeof avatar === 'object') {
      // Avatar is an object
      if (avatar.vrmPath) {
        console.log('[AvatarAssets] Avatar has vrmPath property:', avatar.vrmPath);
        const fullUrl = this.getFullAssetUrl(avatar.vrmPath);
        console.log('[AvatarAssets] Full VRM URL from vrmPath:', fullUrl);
        return fullUrl;
      }
      if (avatar.id) {
        console.log('[AvatarAssets] Avatar has id property:', avatar.id);
        const avatarObj = this.getAvatarById(avatar.id);
        console.log('[AvatarAssets] Found avatar object by ID:', avatarObj);
        const vrmPath = avatarObj ? avatarObj.vrmPath : this.getCurrentData().defaults.fallbackAvatar.vrmPath;
        console.log('[AvatarAssets] Raw VRM path:', vrmPath);
        const fullUrl = this.getFullAssetUrl(vrmPath);
        console.log('[AvatarAssets] Full VRM URL:', fullUrl);
        return fullUrl;
      }
    }
    
    // Default fallback
    console.log('[AvatarAssets] Using default fallback avatar');
    const defaultPath = this.getCurrentData().defaults.fallbackAvatar.vrmPath;
    console.log('[AvatarAssets] Default path:', defaultPath);
    const fullUrl = this.getFullAssetUrl(defaultPath);
    console.log('[AvatarAssets] Default full URL:', fullUrl);
    return fullUrl;
  }

  /**
   * Get Live2D path for an avatar.
   * NOTE: This is based on a convention since live2dPath is not explicitly in avatarAssets.json.
   * It assumes models are in /models/{id}/{id}.model3.json
   * @param {string} avatarId - The ID of the avatar.
   * @returns {string} The constructed path to the Live2D model file.
   */
  getLive2dPath(avatarId) {
    if (!avatarId) {
      console.warn('[AvatarAssets] getLive2dPath called with null/undefined avatarId, falling back to default.');
      // Fallback to a default model if no ID is provided
      return '/models/huohuo/huohuo.model3.json'; 
    }
    // Construct the path based on the convention
    const path = `/models/${avatarId}/${avatarId}.model3.json`;
    console.log(`[AvatarAssets] Constructed Live2D path for ${avatarId}: ${path}`);
    return path;
  }

  /**
   * Get Live2D path for a Live2D avatar object
   * @param {string|Object} avatar - Live2D avatar ID or avatar object
   * @returns {string} Live2D model file path
   */
  getLive2dModelPath(avatar) {
    // Mapping from regular avatar IDs to Live2D avatar IDs
    const regularToLive2dMapping = {
      'm1': 'm1_live2d',
      'm2': 'm2_live2d', 
      'm3': 'm3_live2d',
      'm4': 'm4_live2d'
    };

    if (typeof avatar === 'string') {
      // Avatar is an ID string, map to Live2D avatar ID
      const live2dAvatarId = regularToLive2dMapping[avatar] || avatar;
      const live2dAvatars = this.getLive2dAvatars();
      const avatarObj = live2dAvatars.find(a => a.id === live2dAvatarId);
      const live2dPath = avatarObj ? avatarObj.live2dPath : this.getLive2dPath(avatar);
      return this.getFullAssetUrl(live2dPath);
    }
    
    if (avatar && typeof avatar === 'object') {
      // Avatar is an object
      if (avatar.live2dPath) {
        return this.getFullAssetUrl(avatar.live2dPath);
      }
      if (avatar.id) {
        // Map regular avatar ID to Live2D avatar ID
        const live2dAvatarId = regularToLive2dMapping[avatar.id] || avatar.id;
        const live2dAvatars = this.getLive2dAvatars();
        const avatarObj = live2dAvatars.find(a => a.id === live2dAvatarId);
        const live2dPath = avatarObj ? avatarObj.live2dPath : this.getLive2dPath(avatar.id);
        return this.getFullAssetUrl(live2dPath);
      }
    }
    
    // Default fallback
    return this.getFullAssetUrl('/live2d/models/huohuo/huohuo.model3.json');
  }

  /**
   * Check if VRM file exists and is accessible
   * @param {string} vrmPath - VRM file path
   * @returns {Promise<boolean>} True if file is accessible
   */
  async checkVrmFileExists(vrmPath) {
    try {
      const response = await fetch(vrmPath, {
        method: 'HEAD', // Only check headers, don't download the file
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      console.warn(`⚠️ VRM file not accessible: ${vrmPath}`, error.message);
      return false;
    }
  }

  /**
   * Get VRM path with fallback to local files
   * @param {string|Object} avatar - Avatar ID or avatar object
   * @returns {string} VRM file path (server or local fallback)
   */
  getVrmPathWithFallback(avatar) {
    console.log('[AvatarAssets] getVrmPathWithFallback called with:', avatar);
    
    // If avatar is already a full URL, use it directly
    if (typeof avatar === 'string' && (avatar.startsWith('http://') || avatar.startsWith('https://'))) {
      console.log('[AvatarAssets] Avatar is already a full URL, using directly');
      return {
        server: avatar,
        local: avatar,
        preferred: avatar
      };
    }
    
    const serverPath = this.getVrmPath(avatar);
    
    // If server base URL is configured, check if we should fallback to local
    const currentData = this.getCurrentData();
    if (currentData.config.baseUrl) {
      // Extract the relative path for local fallback
      const relativePath = serverPath.replace(currentData.config.baseUrl, '');
      const localPath = relativePath; // Use relative path for local files
      
      return {
        server: serverPath,
        local: localPath,
        preferred: serverPath
      };
    }
    
    return {
      server: serverPath,
      local: serverPath,
      preferred: serverPath
    };
  }

  /**
   * Get avatar icon path
   * @param {string|Object} avatar - Avatar ID or avatar object
   * @returns {string} Avatar icon path
   */
  getAvatarIcon(avatar) {
    if (typeof avatar === 'string') {
      const avatarObj = this.getAvatarById(avatar);
      const iconPath = avatarObj ? avatarObj.src : this.getCurrentData().defaults.fallbackAvatar.src;
      return this.getFullAssetUrl(iconPath);
    }
    
    if (avatar && typeof avatar === 'object') {
      if (avatar.src) {
        return this.getFullAssetUrl(avatar.src);
      }
      if (avatar.id) {
        const avatarObj = this.getAvatarById(avatar.id);
        const iconPath = avatarObj ? avatarObj.src : this.getCurrentData().defaults.fallbackAvatar.src;
        return this.getFullAssetUrl(iconPath);
      }
    }
    
    const defaultPath = this.getCurrentData().defaults.fallbackAvatar.src;
    return this.getFullAssetUrl(defaultPath);
  }

  /**
   * Get default/fallback avatar
   * @returns {Object} Default avatar object with full URLs
   */
  getDefaultAvatar() {
    const defaultAvatar = this.getCurrentData().defaults.fallbackAvatar;
    return {
      ...defaultAvatar,
      src: this.getFullAssetUrl(defaultAvatar.src),
      vrmPath: this.getFullAssetUrl(defaultAvatar.vrmPath)
    };
  }

  /**
   * Create legacy ID to VRM mapping (for backward compatibility)
   * @returns {Object} Legacy mapping object with full URLs
   */
  getLegacyVrmMapping() {
    const mapping = {};
    const currentData = this.getCurrentData();
    
    // Add primary avatars
    currentData.avatars.forEach(avatar => {
      mapping[avatar.id] = this.getFullAssetUrl(avatar.vrmPath);
    });
    
    // Add alternative avatars
    currentData.alternativeAvatars.forEach(avatar => {
      mapping[avatar.id] = this.getFullAssetUrl(avatar.vrmPath);
    });
    
    // Add legacy mappings
    Object.keys(currentData.legacyMapping).forEach(key => {
      mapping[key] = this.getFullAssetUrl(currentData.legacyMapping[key].vrmPath);
    });
    
    return mapping;
  }

  /**
   * Create legacy ID to icon mapping (for backward compatibility)
   * @returns {Object} Legacy mapping object with full URLs
   */
  getLegacyIconMapping() {
    const mapping = {};
    const currentData = this.getCurrentData();
    
    // Add primary avatars
    currentData.avatars.forEach(avatar => {
      mapping[avatar.id] = this.getFullAssetUrl(avatar.src);
    });
    
    // Add alternative avatars  
    currentData.alternativeAvatars.forEach(avatar => {
      mapping[avatar.id] = this.getFullAssetUrl(avatar.src);
    });
    
    // Add legacy mappings
    Object.keys(currentData.legacyMapping).forEach(key => {
      mapping[key] = this.getFullAssetUrl(currentData.legacyMapping[key].src);
    });
    
    return mapping;
  }

  /**
   * Set server URL for loading assets
   * @param {string} serverUrl - Server URL for assets
   */
  setServerUrl(serverUrl) {
    this.serverUrl = serverUrl;
    this.hasLoadedFromServer = false;
    this.serverData = null;
    this.cache.clear();
  }

  /**
   * Set base URL for loading assets from server (legacy compatibility)
   * @param {string} baseUrl - Base URL for assets
   */
  setBaseUrl(baseUrl) {
    if (baseUrl) {
      this.setServerUrl(`${baseUrl}/avatarAssets.json`);
    }
  }

  /**
   * Get full asset URL (applies base URL to relative paths)
   * @param {string} path - Relative asset path
   * @returns {string} Full asset URL
   */
  getFullAssetUrl(path) {
    // Handle null/undefined paths
    if (!path) {
      console.warn('getFullAssetUrl called with null/undefined path');
      return '';
    }
    
    const currentData = this.getCurrentData();
    
    // If path is already absolute (starts with http/https), return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // If baseUrl is configured, prepend it to relative paths
    if (currentData.config && currentData.config.baseUrl) {
      return `${currentData.config.baseUrl}${path}`;
    }
    
    // Return relative path as-is if no baseUrl
    return path;
  }

  /**
   * Check if data was loaded from server
   * @returns {boolean} True if loaded from server
   */
  isLoadedFromServer() {
    return this.hasLoadedFromServer && this.serverData !== null;
  }

  /**
   * Get data source information
   * @returns {Object} Information about data source
   */
  getDataSource() {
    return {
      source: this.isLoadedFromServer() ? 'server' : 'local',
      url: this.isLoadedFromServer() ? this.serverUrl : 'local file',
      lastLoaded: this.hasLoadedFromServer ? new Date().toISOString() : null
    };
  }

  /**
   * Ensure an avatar object has full URLs (converts relative paths to full URLs)
   * @param {Object} avatar - Avatar object that might have relative paths
   * @returns {Object} Avatar object with full URLs
   */
  ensureFullUrls(avatar) {
    if (!avatar) return null;
    
    try {
      return {
        ...avatar,
        src: avatar.src ? this.getFullAssetUrl(avatar.src) : (avatar.id ? this.getAvatarIcon(avatar.id) : ''),
        image: avatar.image ? this.getFullAssetUrl(avatar.image) : (avatar.id ? this.getAvatarIcon(avatar.id) : ''),
        vrmPath: avatar.vrmPath ? this.getFullAssetUrl(avatar.vrmPath) : (avatar.id ? this.getVrmPath(avatar.id) : '')
      };
    } catch (error) {
      console.error('Error in ensureFullUrls:', error, 'Avatar:', avatar);
      return avatar; // Return original avatar if there's an error
    }
  }

  /**
   * Ensure a user object has full URLs for the selected avatar
   * @param {Object} user - User object that might have relative avatar paths
   * @returns {Object} User object with full avatar URLs
   */
  ensureUserAvatarUrls(user) {
    if (!user) return user;
    
    try {
      if (!user.selectedAvatar) return user;
      
      return {
        ...user,
        selectedAvatar: this.ensureFullUrls(user.selectedAvatar)
      };
    } catch (error) {
      console.error('Error in ensureUserAvatarUrls:', error, 'User:', user);
      return user; // Return original user if there's an error
    }
  }
}

// Create singleton instance
const avatarAssets = new AvatarAssetsManager();

export default avatarAssets;
export { AvatarAssetsManager };
